'use client'

import { Box, Stack } from '@mantine/core'
import { DataTable, type DataTableSortStatus } from 'mantine-datatable'
import { useShallow } from 'zustand/react/shallow'

import useSubscribeLiveCoinsMarket from '@/hooks/business/useSubscribeLiveCoinsMarket'
import { useI18nRouter } from '@/i18n/navigation'

import { CRYPTO_FETCH_BLOCK_SIZE, CRYPTO_PAGE_SIZE } from '../../CoinsTable/configs'
import { useCoinsTableStore } from '../../CoinsTable/context'
import type { TableRecord } from '../../CoinsTable/Table/columns/configs'
import EmptyState from '../../CoinsTable/Table/EmptyState'
import { useCoinRestrictedAreas } from '../../CoinsTable/Table/hooks/useCoinRestrictedAreas'
import useTableData from '../../CoinsTable/Table/hooks/useTableData'
import CursorPagination from '../StockTable/CursorPagination'
import styles from '../style.module.css'
import { useHomeCoinsColumns } from './columns'

interface DesktopCoinsTableProps {
  initialSortStatus: DataTableSortStatus<TableRecord>
}

export default function DesktopCoinsTable({ initialSortStatus }: DesktopCoinsTableProps) {
  const router = useI18nRouter()

  const { currentPage, updateFilters, updateLiveCoinsMarket, clearLiveCoinsMarket } =
    useCoinsTableStore(
      useShallow(state => ({
        currentPage: state.currentPage,
        updateFilters: state.updateFilters,
        updateLiveCoinsMarket: state.updateLiveCoinsMarket,
        clearLiveCoinsMarket: state.clearLiveCoinsMarket,
      })),
    )

  const { isPending, records, totalRecords } = useTableData({
    initialSortStatus,
    pageSize: CRYPTO_PAGE_SIZE,
    sortInRequest: true,
    fetchBlockSize: CRYPTO_FETCH_BLOCK_SIZE,
  })

  useSubscribeLiveCoinsMarket(
    records.map(coin => coin.slug),
    {
      updateLiveCoinsMarket,
      clearLiveCoinsMarket,
    },
  )

  const { restrictedAreasMap, visibleCoins } = useCoinRestrictedAreas(records)
  const columns = useHomeCoinsColumns(restrictedAreasMap)

  if (visibleCoins.length === 0) {
    return <EmptyState />
  }

  return (
    <Box pos="relative">
      <Stack gap="16px">
        <DataTable
          fetching={isPending}
          textSelectionDisabled
          records={visibleCoins}
          columns={columns}
          defaultColumnProps={{
            titleClassName: 'typography-body',
            cellsClassName: `typography-body-number ${styles.tableCell}`,
          }}
          idAccessor="token_id"
          pinFirstColumn
          onRowClick={({ record }: { record: TableRecord }) => {
            router.push(`/${record.slug}`)
          }}
          classNames={{ root: styles.root, header: styles.header }}
          minHeight="500px"
          backgroundColor="var(--bg-app)"
          withTableBorder={false}
          withColumnBorders={false}
          withRowBorders={false}
          highlightOnHover
          highlightOnHoverColor="var(--bg-surface-secondary)"
        />
        <CursorPagination
          currentPage={currentPage}
          loadedPageCount={Math.ceil(totalRecords / CRYPTO_PAGE_SIZE)}
          totalRecords={totalRecords}
          isFetching={isPending}
          recordsPerPage={CRYPTO_PAGE_SIZE}
          onChange={page => {
            updateFilters({ currentPage: page })
          }}
        />
      </Stack>
    </Box>
  )
}
